/**
 * 中文（简体）本地回退文案
 *
 * 当远程 `icua-i18n` 加载失败或返回错误时，由 watch-lang 中的后端注入。
 * 维护说明：与页面中 `useTranslation('命名空间')` 及 `t('key')` 保持一致；复杂结构需满足 `returnObjects: true` 的类型。
 *
 * @author 前端团队
 * @module locales/zh-cn-fallback/namespaces
 */

/** 各命名空间下的资源（嵌套对象会与 i18next 默认解析一致） */
export const zhCnFallbackNamespaces: Record<string, Record<string, unknown>> = {
  Header: {
    home: '主页',
    'news center': '新闻中心',
    'Flagship Initiatives': '旗舰项目',
    'smart education platform': 'E4T智慧教育平台',
    'UNESCO ASPnet': 'UNESCO ASPnet',
    '2026 aspnet global conference': '2026年联系学校网络全球会议',
    partners: '合作伙伴',
    'about us': '关于我们',
    search: '搜索',
    'search placeholder': '请输入关键词',
    'close search': '关闭搜索',
  },

  Footer: {
    'organization full name': '联合国教科文组织联系学校网络国际中心(ICUA)',
    'contact section title': '联系我们',
    'copyright rights': '© 2026 ICUA. 保留所有权利.',
    'copyright credit': '技术支持由华师云智能提供',
    /** @deprecated 请使用 copyright rights + copyright credit */
    'copyright line': '© 2026 ICUA. 保留所有权利 技术支持由华师云智能提供',
    'apply now': '联系我们',
    list: [
      { title: '邮箱', href: 'info@icua.org', hrefType: 'mailto' },
      { title: '电话', href: '+86-899-88344287', hrefType: 'tel' },
      {
        title: '地址',
        href: '海南省三亚市崖州区创意产业园新道街用友产业园(三亚)11号楼',
      },
    ],
  },

  Index: {
    'home hero banner alt': 'ICUA 官网首页主视觉',
    /** H5 首页副标题 */
    'home hero subtitle': '连接全球学校，推动可持续教育',
    'home hero meeting detail': '查看会议详情',
    'home hero meeting video': '观看年会视频',
    'home hero activity detail': '查看活动详情',
    'home hero activity detail url': 'https://www.icua-cipp.com/zh/activity/2026',
    news: '新闻动态',
    /** 嵌套键，供 `t('home.newsTitle')` */
    home: {
      newsTitle: '新闻动态',
      activityTitle: '国际节日寄语海报',
    },
    'more news': '查看更多',
    'read full article': '阅读全文',
    'home international posters': '国际节日寄语海报',
    'home center intro title': '中心介绍',
    'home center intro paragraph 1':
      '联合国教科文组织联系学校网络国际中心（以下简称为"国际中心"），作为联合国教科文组织支持的教育类二类中心，于2021年正式落户海南省。',
    'home center intro paragraph 2':
      'UNESCO一类以及二类机构或中心，在制度上是UNESCO不可分割的组成部分。全球现有10个一类机构，14个教育类二类中心。',
    'home center intro paragraph 3':
      '国际中心作为沟通和合作的桥梁，旨在通过知识生产、能力培养、技术支持和信息共享，积极参与教科文组织相关工作，为实现"可持续发展目标4—2030年教育"作出贡献。',
    'home center intro paragraph 4':
      '联合国教科文组织联系学校网络（以下简称为"联校网"）成立于1953年，现其成员涵盖180个国家近10000多所学校。联校网围绕"树立儿童和青年的保护和平意识"这一目标，联系世界各地的学校等教育机构，致力于在实践中支持国际理解、和平、文化间对话、可持续发展和优质教育。',
    'home center intro video cover alt': '中心介绍视频封面',
    'home center intro play': '播放中心介绍视频',
    'home center intro video title': '中心介绍视频',
    'home center intro video url': 'https://static.huashicloud.com/assets/ka/icua/video/center-intro1.mov',
    'home center intro video unsupported': '当前浏览器不支持视频播放，请升级浏览器后重试。',
    'smart education platform': 'E4T智慧教育平台',
    'smart education platform desc':
      '通过教育变革推动全球学生的全面发展与福祉，共建可持续未来。ICUA以“福祉”为核心，链接世界各地的学校和青少年。ICUA正在开发智慧教育平台，推动跨文化教育实践共同体的建设，为会员学校在可持续发展目标指引下共同分享、学习和行动创设新的空间。',
    'visit platform': '了解更多',
    'smart education platform diagram alt': 'E4T 智慧教育平台示意图',
    /** H5 首页区与 Smart Education 区块共用 */
    'smart education hero headline': '通过教育变革推动全球学生的全面发展与福祉，共建可持续未来。',
    'home hero background image': 'home/icua-home3.jpeg',
    /** 年会视频弹窗（MeetingVideoButton，Index 命名空间） */
    common: {
      cancel: '取消',
      close: '关闭',
      confirm: '确认',
    },
    'home meeting video empty': '资源将在会期间更新，敬请期待～',
    'home meeting video date prefix': '会议日期：',
    'home meeting video error message': '视频资源加载失败，请稍后重试。',
    'home meeting video error title': '加载失败',
    'home meeting video got it': '我知道了',
    'home meeting video modal title': '年会视频',
    'home meeting video open new tab': '若播放器无法正常播放，请点击下方链接在新窗口打开。',
    'home meeting video password desc': '请输入密码后即可观看年会视频。',
    'home meeting video password error': '密码错误，请重新输入。',
    'home meeting video password placeholder': '请输入密码',
    'home meeting video password title': '请输入观看密码',
    'home meeting video tips title': '提示',
    'home meeting video unknown date': '未标注日期',
  },

  Partners: {
    'contact us': '联系我们',
    'join us': '加入我们，成为伙伴',
    /** H5 合作伙伴页提示文案 */
    'partners join us tip': '如需申请合作，请通过底部“联系我们”入口提交信息。',
    partnersSectionUniversities: '高等院校',
    partnersSectionFutureCreators: '2024届“未来开创者——可持续发展主题创意征集活动合作伙伴”',
    partnersSubNavAria: '合作伙伴分类',
    'click to enter the official website': '进入官网',
    'view more': '查看更多',
  },

  'About Us': {
    'about us': '关于我们',
    aboutIntro: '简介',
    visionAndMission: '愿景与使命',
    functions: '功能',
    membersIntro: '成员介绍',
    vision: '愿景',
    'vision desc': '通过教育变革推动全球学生的全面发展与福祉，共建可持续未来。',
    mission: '使命',
    missionFeatures: [],
    missionCardFallback1:
      '联合国教科文组织联系学校网络国际中心（ICUA）致力于促进可持续发展教育（ESD）、跨文化学习和社会包容，推动全球学生的全面发展与福祉。',
    missionCardFallback2:
      'ICUA专注于知识生产、青年赋权、能力建设与国际合作，赋能学生、教育工作者和社区，共同创造和平、包容和可持续的未来。',
    'platform introduction desc':
      '联合国教科文组织联系学校网络国际中心（以下简称为"国际中心"），作为联合国教科文组织支持的教育类二类中心，于2021年正式落户海南省。\n\n UNESCO一类以及二类机构或中心，在制度上是UNESCO不可分割的组成部分。全球现有10个一类机构，14个教育类二类中心。\n\n国际中心作为沟通和合作的桥梁，旨在通过知识生产、能力培养、技术支持和信息共享，积极参与教科文组织相关工作，为实现"可持续发展目标4—2030年教育"作出贡献。\n\n联合国教科文组织联系学校网络（以下简称为"联校网"）成立于1953年，现其成员涵盖180个国家近10000多所学校。联校网围绕"树立儿童和青年的保护和平意识"这一目标，联系世界各地的学校等教育机构，致力于在实践中支持国际理解、和平、文化间对话、可持续发展和优质教育。',
    functionsList: [
      {
        title: '技术支持',
        image: 'about-us/support-icon.svg',
        descs: [
          '为相关学校提供政策的、专业的与技术的支持性服务，鼓励 ASPnet 学生和教师通过冬/夏令营、暑期学校、短期游学及信息技术应用培训等各种教育活动展开交流。',
        ],
      },
      {
        title: '信息共享',
        image: 'about-us/share-icon.svg',
        descs: [
          '运用云计算、大数据及人工智能技术建立资源信息网络平台。组织年度ASPnet 国际论坛，交流ASPnet成功经验和最佳实践模式。',
        ],
      },
      {
        title: '知识生产',
        image: 'about-us/knowledge-icon.svg',
        descs: ['研究联合国教科文组织会员国的办学趋势及新需求。'],
      },
      {
        title: '能力发展',
        image: 'about-us/develop-icon.svg',
        descs: [
          '具体关注亚太地区，面向 ASPnet 中小学，针对各类与ASPnet 相关的主题开展培训活动。',
        ],
      },
    ],
    governingList: [
      {
        department: '理事会主席',
        members: [{ position: '海南省人民政府副省长', name: '谢京' }],
      },
      {
        department: '理事会成员',
        members: [
          {
            position: '联合国教科文组织总干事代表，联合国教科文组织联系学校网络（ASPnet）国际协调员',
            name: '莉迪娅·鲁普雷希特',
          },
          {
            position: '中国联合国教科文组织全国委员会秘书长',
            name: '秦昌威',
          },
          {
            position: '海南省教育厅厅长',
            name: '丁晖',
          },
          {
            position: '海南省三亚市人民政府副市长',
            name: '尹承玲',
          },
          {
            position: '联合国教科文组织教育项目官员',
            name: '裴伯庸',
          },
          {
            position: '阿曼联合国教科文组织联系学校网络（ASPnet）国家协调员',
            name: '尤斯里娅·苏莱曼·阿尔哈提',
          },
          {
            position: '香港大学联合国教科文组织比较教育教席',
            name: '贝磊',
          },
          {
            position: '华中师范大学教授',
            name: '刘延申',
          },
          {
            position: '海南中学党委书记',
            name: '柳海英',
          },
          {
            position: '海南省教育研究培训院院长',
            name: '孙自强',
          },
          {
            position: '联合国教科文组织联系学校网络国际中心拟任主任',
            name: '俞立中',
          },
        ],
      },
    ],
    aboutTeams: [
      {
        department: '领导层',
        members: [
          { position: '主任', name: '俞立中' },
          { position: '执行主任', name: '张薇' },
        ],
      },
      {
        department: '可持续发展教育与创新办公室',
        members: [
          { position: '项目协调员', name: '徐星若、于普' },
        ],
      },
      {
        department: '战略规划与行政办公室',
        members: [
          { position: '办公室主任', name: '高原' },
          { position: '行政助理', name: '陈宜卓' },
        ],
      },
      {
        department: '跨文化与遗产教育办公室',
        members: [
          { position: '办公室主任', name: '李行方' },
          { position: '项目协调员', name: '林宜卿' },
        ],
      },
    ],
    aboutAcademicGuidanceUnitList: [
      { title: '华东师范大学课程与教学研究所', href: '' },
      { title: 'ICUA Fellow', href: '' },
    ],
    councilMembersListTitle: '联合国教科文组织联系学校网络国际中心理事会成员名单',
    aboutTeamSectionTitle: '执行团队',
    aboutTeamIntro:
      '联合国教科文组织联系学校网络国际中心（ICUA）由一支专业团队提供支持，团队成员致力于推动全球教育、文化交流与可持续发展。',
    aboutAcademicGuidanceUnit: '学术指导单位',
    internBackgroundCheck: '实习生招募详情',
    internBackgroundCheckUrl: '',
    internBackgroundCheckDesc:
      '实习生实习经历核实授权第三方机构(如用人单位或院校)如需核实某位申请人在ICUA的实习经历，请通过以下官方渠道联系ICUA战略规划与行政办公室：',
    internBackgroundCheckContact: '联系方式:',
    internBackgroundCheckEmailLabel: '官方邮箱:',
    internBackgroundCheckPhoneLabel: '联系电话:',
    internBackgroundCheckTip: '@所有核实请求必须包含实习生的全名、实习期间以及查询目的的简要说明。',
    /** H5 关于我们信息卡副文案 */
    'mission desc': '联合国教科文组织联系学校网络国际中心（ICUA）致力于促进可持续发展教育（ESD）、跨文化学习和社会包容，推动全球学生的全面发展与福祉。\n\n ICUA专注于知识生产、青年赋权、能力建设与国际合作，赋能学生、教育工作者和社区，共同创造和平、包容和可持续的未来。',
    functionsDesc: '开展教育交流、能力建设与全球合作项目。',
    'about us hot info': '热点介绍',
    'about us fill in questionnaire': '立即填写报名信息',
  },

  /** 会议专题（PC 页内语切换 + H5 会议导航） */
  Conference: {
    'conference hero title': '2026年全球联系学校网络会议',
    'conference hero subtitle': 'ICUA Global ASPnet Conference 2026',
    'conference home': '会议首页',
    'conference agenda': '暂定议程',
    'conference session': '会议信息与指南',
    'conference posters': '会议海报',
    'conference section title': '会议信息',
    'conference posters section title': '会议海报展示',
    'conference mobile placeholder':
      '该页面为 H5 端会议导航入口，按选中的标签展示对应会议内容摘要。',
    'conference agenda section title': '暂定议程',
    'conference agenda summary': '按日程查看会议安排与关键时间节点。',
    'conference session section title': '会议信息与指南',
    'conference session summary': '查看会务安排、参会须知与联系方式。',
    'conference posters summary': '展示来自各国学校与组织的会议海报。',
    'conference disclaimer title': '免责声明',
    'conference disclaimer text':
      '海报中所表达的观点和意见仅代表作者本人，并不一定反映联合国教科文组织联系学校网络（ASPnet）与ICUA的立场。',
  },

  Global: {
    'not found': '页面不存在',
    'uncatch error': '发生未知错误',
    'back to home': '返回首页',
    'no data': '暂无数据',
    confirm: '确认',
  },

  LinkPagination: {
    pageSize: '每页 {{pageSize}} 条',
    total: '共 {{total}} 条',
    'prev page': '上一页',
    'next page': '下一页',
  },

  translation: {
    collectForm: {
      success: '提交成功，感谢您的反馈！',
    },
    'collectForm.success': '提交成功，感谢您的反馈！',
    common: {
      pleaseSelect: '请选择',
      submit: '提交',
      submitting: '提交中…',
    },
    'common.pleaseSelect': '请选择',
    'common.submit': '提交',
    'common.submitting': '提交中…',
  },

  'News Center': {
    'news center': '新闻中心',
    'activity time': '活动时间',
  },

  Activity: {
    'international conference and activities': '国际会议与活动',
    'view details': '查看详情',
  },

  Search: {
    title: '搜索结果',
    resultCount: '共找到 {{count}} 条结果',
  },

  Database: {
    title: '知识库',
    download: '下载',
  },

  'Smart Education Platform': {
    'E4T intro card title': 'E4T智慧教育平台简介',
    'E4T intro body':
      '“Education for Tomorrow (E4T) 智慧教育平台”系由国际中心自主设计、建设并运营的全球领军项目，旨在通过教育创新与国际合作，推动全球教育实践共同体的构建。平台紧密对接联合国2030年可持续发展议程，重点关注可持续发展目标4（优质教育），并与性别平等、全民健康、气候行动、国际理解教育、非物质文化遗产保护及跨文化交流等相关目标协同推进。\n\n E4T平台通过整合全球优质教育资源、构建数字化协作网络、推动学校变革与教师专业发展，为全球教育工作者提供促进知识共享与跨文化合作的综合性数字平台。',
    'platform intro cover': 'smart-education-platform/e4t-intro.jpg',
    'smart education hero headline': '通过教育变革推动全球学生的全面发展与福祉，共建可持续未来',
    'visit E4T platform cta': '访问 E4T 智慧教育平台',
    'platform intro section title': 'E4T智慧教育平台简介',
    'platform functions section title': '平台功能',
    coreFunctions: [],
    'feature card 1 title': '智慧连接',
    'feature card 1 desc': '构建教育者与机构的数字化协作网络；',
    'feature card 1 cover': 'smart-education-platform/e4t-feature-connect.png',
    'feature card 1 icon': 'smart-education-platform/e4t-connect-icon.svg',
    'feature card 2 title': '资源共享',
    'feature card 2 desc': '聚合全球优质教育资源，促进跨区域教育公平',
    'feature card 2 cover': 'smart-education-platform/e4t-feature-share.png',
    'feature card 2 icon': 'smart-education-platform/e4t-share-icon.svg',
    'feature card 3 title': '政策与趋势',
    'feature card 3 desc': '支持教育者追踪全球教育政策与发展动态；',
    'feature card 3 cover': 'smart-education-platform/e4t-feature-policy.png',
    'feature card 3 icon': 'smart-education-platform/e4t-policy-icon.svg',
    'feature card 4 title': '专业支持',
    'feature card 4 desc': '提供教师培训、校际结对与领导力建设等实践支持。',
    'feature card 4 cover': 'smart-education-platform/e4t-feature-support.png',
    'feature card 4 icon': 'smart-education-platform/e4t-support-icon.svg',
  },
};
